<script setup lang="ts">
/**
 * TermsView — пользовательское соглашение (inbox-337).
 *
 * RU baseline (ГК РФ + 149-ФЗ + 153-ФЗ). Перед production — review @user/legal.
 */
import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead } from '@/composables/usePageHead';

usePageHead({
  title: 'Условия использования',
  description: 'Пользовательское соглашение сервиса link.helmix.app.',
  path: '/terms',
  type: 'website',
});
</script>

<template>
  <AppPublic>
    <article class="legal">
      <div class="container">
        <header class="head">
          <span class="eyebrow">Условия использования</span>
          <h1>Пользовательское соглашение link.helmix.app</h1>
          <p class="meta">Действует с 21 мая 2026. Версия 1.0.</p>
        </header>

        <section>
          <h2>1. Стороны</h2>
          <p>
            Настоящее соглашение заключается между ООО «Хелмикс Лабс» (далее — «Сервис»,
            «мы») и физическим или юридическим лицом, использующим Сервис (далее —
            «Пользователь»). Регистрация аккаунта означает безусловное согласие с
            настоящим документом.
          </p>
        </section>

        <section>
          <h2>2. Предмет</h2>
          <p>
            Сервис предоставляет облачную платформу для сокращения URL, генерации QR-кодов,
            публикации bio-страниц и сбора аналитики кликов. Доступ предоставляется по
            модели SaaS — без передачи исключительных прав на ПО.
          </p>
        </section>

        <section>
          <h2>3. Тарифы и платежи</h2>
          <ul>
            <li>Тарифы: Free / Старт / Бизнес / Команда / Enterprise. Цены указаны на
                странице <a href="/pricing">/pricing</a>.</li>
            <li>Оплата ежемесячно или с годовой скидкой 20%.</li>
            <li>Закрывающие документы (счёт, акт, счёт-фактура) — на каждое списание для
                юридических лиц по ОКВЭД.</li>
            <li>Возврат — в течение 14 дней с даты оплаты при отсутствии существенного
                использования.</li>
          </ul>
        </section>

        <section>
          <h2>4. Запрещённое использование</h2>
          <p>Пользователю запрещено использовать Сервис для:</p>
          <ul>
            <li>Распространения вредоносного ПО, фишинга, мошенничества.</li>
            <li>Контента, нарушающего законодательство РФ (экстремизм, призывы к насилию,
                наркотики, CP).</li>
            <li>Массовых рассылок без согласия получателей (CAN-SPAM / 38-ФЗ).</li>
            <li>Reverse engineering, обход технических мер защиты, попыток нарушения
                целостности Сервиса.</li>
          </ul>
          <p>
            При обнаружении нарушений Сервис вправе приостановить или удалить аккаунт
            без возврата средств.
          </p>
        </section>

        <section>
          <h2>5. Лимиты и ответственность</h2>
          <ul>
            <li>SLA: 99,9% для тарифов Старт+, 99,99% для Enterprise.</li>
            <li>Ответственность Сервиса ограничена суммой оплаты за месяц.</li>
            <li>Сервис не несёт ответственности за контент, размещаемый Пользователем
                по своим коротким ссылкам.</li>
          </ul>
        </section>

        <section>
          <h2>6. Расторжение</h2>
          <p>
            Пользователь вправе расторгнуть соглашение в любой момент через удаление
            аккаунта в /settings/profile. Сервис вправе расторгнуть при нарушении п. 4
            или просрочке оплаты более 30 дней.
          </p>
        </section>

        <section>
          <h2>7. Изменения</h2>
          <p>
            Сервис уведомляет о существенных изменениях за 14 дней через email и баннер
            в личном кабинете. Продолжение использования после уведомления = согласие
            с обновлённой редакцией.
          </p>
        </section>

        <section>
          <h2>8. Применимое право и споры</h2>
          <p>
            Соглашение регулируется законодательством Российской Федерации. Споры
            рассматриваются в Арбитражном суде г. Москвы. Pre-trial: претензия на
            <a href="mailto:legal@helmix.app">legal@helmix.app</a>, срок ответа 30 дней.
          </p>
        </section>

        <p class="foot-note">
          ⚠ Это базовая редакция. Перед публичным запуском — review @user/legal на
          актуальность ГК РФ, 149-ФЗ, 153-ФЗ, 38-ФЗ.
        </p>
      </div>
    </article>
  </AppPublic>
</template>

<style scoped>
.legal { padding: 48px 0 96px; color: #c5cdd6; line-height: 1.65; }
.container { max-width: 760px; margin: 0 auto; padding: 0 24px; }
.head { text-align: center; margin-bottom: 40px; }
.eyebrow { display: inline-block; padding: 4px 12px; background: rgba(79,140,255,0.10); color: #6f9eff; border-radius: 100px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 14px; }
.head h1 { font-size: 32px; line-height: 1.2; letter-spacing: -0.02em; font-weight: 700; color: #e8edf3; margin-bottom: 8px; }
.meta { font-size: 13px; color: #8b95a5; }
section { margin-bottom: 28px; }
section h2 { font-size: 18px; font-weight: 600; color: #e8edf3; margin-bottom: 10px; }
section p, section ul { font-size: 14.5px; }
section ul { padding-left: 22px; }
section li { margin-bottom: 6px; }
section a { color: #6f9eff; }
.foot-note { margin-top: 36px; padding: 14px 18px; background: rgba(245,165,36,0.08); border-left: 3px solid #f5a523; border-radius: 4px; font-size: 13px; color: #c5cdd6; }
</style>
